import { useEffect, useRef, useState } from 'react';
import { BuildingOffice2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { usePracticeSearch } from '../../../hooks/usePracticeSearch';
import { PracticeChip } from '../../accounts/users/PracticeChip';

interface PracticeScopeSearchProps {
    selectedId: string;
    selectedLabel: string | null;
    onSelect: (practiceId: string, name: string | null) => void;
    onClear: () => void;
}

// Single-select sibling of shared/PracticeMultiSelect: same search-then-pick
// interaction (usePracticeSearch, the same debounced server search),
// collapsed to one target instead of a linked list.
export function PracticeScopeSearch({
    selectedId,
    selectedLabel,
    onSelect,
    onClear
}: PracticeScopeSearchProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const { results, isLoading } = usePracticeSearch(search, open);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const handlePick = (practiceId: string, name: string | null) => {
        onSelect(practiceId, name);
        setSearch('');
        setOpen(false);
    };

    return (
        <div ref={containerRef} className="relative">
            {selectedId ? (
                <div className="h-10 border border-gray-200 rounded-lg px-3 flex items-center bg-white">
                    <PracticeChip name={selectedLabel} id={selectedId} onRemove={onClear} />
                </div>
            ) : (
                <span
                    className={`h-10 border rounded-lg px-3 flex items-center gap-2 ${
                        open
                            ? 'bg-white border-gray-800 ring-2 ring-gray-200'
                            : 'bg-white border-gray-300'
                    }`}
                >
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onFocus={() => setOpen(true)}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setOpen(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') setOpen(false);
                        }}
                        placeholder="Search a practice"
                        className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                    />
                </span>
            )}

            {open && !selectedId && (
                <div className="absolute left-0 right-0 top-11 z-40 bg-white border border-gray-200 rounded-xl shadow-[0_12px_28px_rgba(0,0,0,0.14)] overflow-hidden">
                    <div className="py-1 max-h-[220px] overflow-y-auto">
                        {isLoading && (
                            <div className="px-3 py-2 text-[12px] text-gray-400">Searching…</div>
                        )}
                        {!isLoading && results.length === 0 && (
                            <div className="px-3 py-2 text-[12px] text-gray-400">
                                No practices found.
                            </div>
                        )}
                        {!isLoading &&
                            results.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => handlePick(p.id, p.name)}
                                    className="w-full px-3 h-11 flex items-center gap-2.5 text-left hover:bg-gray-50"
                                >
                                    <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] font-semibold text-gray-900 truncate">
                                            {p.name}
                                        </span>
                                    </span>
                                </button>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}
